/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const UNPAUSE_DISCRIMINATOR = new Uint8Array([
  169, 144, 4, 38, 10, 141, 188, 255,
]);

export function getUnpauseDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(UNPAUSE_DISCRIMINATOR);
}

export type UnpauseInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      TAccountAdmin extends string
        ? ReadonlySignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      ...TRemainingAccounts,
    ]
  >;

export type UnpauseInstructionData = { discriminator: ReadonlyUint8Array };

export type UnpauseInstructionDataArgs = {};

export function getUnpauseInstructionDataEncoder(): FixedSizeEncoder<UnpauseInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({ ...value, discriminator: UNPAUSE_DISCRIMINATOR }),
  );
}

export function getUnpauseInstructionDataDecoder(): FixedSizeDecoder<UnpauseInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getUnpauseInstructionDataCodec(): FixedSizeCodec<
  UnpauseInstructionDataArgs,
  UnpauseInstructionData
> {
  return combineCodec(
    getUnpauseInstructionDataEncoder(),
    getUnpauseInstructionDataDecoder(),
  );
}

export type UnpauseInput<
  TAccountConfig extends string = string,
  TAccountAdmin extends string = string,
> = {
  config: Address<TAccountConfig>;
  admin: TransactionSigner<TAccountAdmin>;
};

export function getUnpauseInstruction<
  TAccountConfig extends string,
  TAccountAdmin extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: UnpauseInput<TAccountConfig, TAccountAdmin>,
  config?: { programAddress?: TProgramAddress },
): UnpauseInstruction<TProgramAddress, TAccountConfig, TAccountAdmin> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('admin', accounts.admin),
    ],
    data: getUnpauseInstructionDataEncoder().encode({}),
    programAddress,
  } as UnpauseInstruction<TProgramAddress, TAccountConfig, TAccountAdmin>);
}

export type ParsedUnpauseInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    admin: TAccountMetas[1];
  };
  data: UnpauseInstructionData;
};

export function parseUnpauseInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUnpauseInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { config: getNextAccount(), admin: getNextAccount() },
    data: getUnpauseInstructionDataDecoder().decode(instruction.data),
  };
}
